import {UserStateLoaded} from 'types/userState/userState';
import {ACCESS_LEVELS} from '../../pages/trafficsim/utils/access_levels.ts';

/**
 * Primitive method of testing access by comparing the key and localStorage group
 * to the tabAccess
 *
 * @param key Access key to check, e.g. "train" or "rails"
 * @param userState The authenticated state of the user
 */
export const hasAccess = (key: string, userState: UserStateLoaded): boolean => {
  const group = userState.access.group;
  const applicationAccess = userState.access.applicationAccess;
  return group === 'sysadmin' || applicationAccess.includes(key);
};
/**
 * Same as hasAccess but excludes admin access so admins don't see org specific apps without logging
 * in as that org
 * @param key
 * @param userState
 */
export const hasApplicationAccess = (
  key: string,
  userState: UserStateLoaded,
): boolean => {
  const applicationAccess = userState.access.applicationAccess;
  return applicationAccess.includes(key);
};

/**
 * Returns true if the user as an administrator, false otherwise.
 *
 * @param userState The authenticated state of the user
 */
export const hasAdminAccess = (userState: UserStateLoaded): boolean => {
  return userState?.access?.group === 'sysadmin';
};

/**
 * Returns true if the user as a TracfficSim admin, false otherwise.
 *
 * @param userState The authenticated state of the user
 */
export const hasTrafficSimAdminAccess = (userState: UserStateLoaded): boolean => {
  return (
    hasAdminAccess(userState) ||
    extractTrafficSimAccess(userState) === ACCESS_LEVELS.ADMIN
  );
};

/**
 * Returns trafficsim access level from the userstate.
 *
 * @param userState The authenticated state of the user
 */
export const extractTrafficSimAccess = (userState: UserStateLoaded): string => {
  const trafficSimAccess = userState.access.applicationAccess;
  const accessLevels = Array.from(trafficSimAccess).filter(
    (s) => s.substring(0, 9) === 'tracksim_',
  );
  return accessLevels.length > 0 ? accessLevels[0] : '';
};

// Token type when raw token string is missing
export type TokenMissingType = {
  // Raw token string
  raw: undefined;
  // Contect for what provided or updated the value, for troubleshooting purposes
  context: string;
  contextLog: string[];
};
// Token type when raw token string is present
export type TokenPresentType = {
  // Raw token string
  raw: string;
  // Contect for what provided or updated the value, for troubleshooting purposes
  context: string;
  contextLog: string[];
};
export type TokenType = TokenMissingType | TokenPresentType;

export const parseStoredToken = (tokenJson: string | undefined): string | undefined => {
  let value: string | undefined = undefined;
  if (tokenJson) {
    try {
      const tokenData = JSON.parse(tokenJson);
      if (typeof tokenData === 'string') {
        // Older storage value which is stored as token string only
        value = tokenData;
      }
    } catch (error) {
      value = tokenJson;
    }
  }
  return value;
};

export const makeEmptyToken = (context: string = 'unknown'): TokenType => {
  return {
    raw: undefined,
    context: context,
    contextLog: context !== 'unknown' ? [context] : [],
  };
};

export const mergeContextLog = (contextLog: string[], newContext: string): string[] => {
  let newLog = contextLog.slice();
  if (contextLog.length > 0 && contextLog[contextLog.length - 1] === 'unknown') {
    newLog = contextLog.slice(0, -1);
  }
  if (contextLog.length > 0 && contextLog[contextLog.length - 1] === newContext) {
    newLog = contextLog.slice(0, -1);
  }
  if (newContext !== 'unknown') {
    newLog.push(newContext);
  }
  return newLog;
};

/**
 * Pulls the JSON stringified token from local storage when appProps.token isn't available
 */
export const tokenFromStorage = (initiator: string): TokenType => {
  const tokenData = localStorage.getItem('token') as string | undefined;
  const storedToken = parseStoredToken(tokenData);
  if (process.env.REACT_AUTH_DEBUG && console.log) {
    console.log('tokenFromStorage', {initiator, value: storedToken});
  }
  return storedToken !== undefined
    ? {
        raw: storedToken,
        context: 'local-storage',
        contextLog: ['local-storage'],
      }
    : makeEmptyToken();
};

/**
 * The token in local storage, or undefined when there is none or the user logged out
 */
export const getStoredTokenOrUndefined = (initiator: string): TokenType => {
  let token: TokenType;
  try {
    token = tokenFromStorage(initiator);
  } catch {
    // Logging out stores the string "undefined", which is not valid JSON
    token = makeEmptyToken();
  }
  return token;
};

/**
 * Headers that authenticate a request to the Gothenburg API, which reads the login
 * JWT from `JWT_TOKEN` (its `JWT_KEY_NAME` setting). Empty when not logged in, so
 * the API answers 401.
 */
export const gothenburgApiAuthHeaders = (): Record<string, string> => {
  const token = process.env.REACT_APP_FORCED_TOKEN
    ? {
        raw: process.env.REACT_APP_FORCED_TOKEN,
        context: '',
      }
    : getStoredTokenOrUndefined('gothenburgApiAuthHeaders');
  return token.raw ? {JWT_TOKEN: token.raw} : {};
};
